import { useMemo, useRef, useState } from 'react';
import { Check, Search, X } from 'lucide-react';
import { LevelChip, MobileSheet, initials, useSheetIds } from './mobileUi';
import { useT } from '../../i18n';

export interface RecipientPickerProps {
  players: any[];
  /** The member writing the message — never listed. */
  meId: any;
  clubName: string;
  /** Hidden when the member cannot write to the club (no club in this install). */
  allowClub?: boolean;
  onPickClub: () => Promise<void> | void;
  /** One id → dm; several → group (title optional). */
  onPickMembers: (ids: any[], title: string) => Promise<void> | void;
  onClose: () => void;
}

// «Ny melding» — a bottom sheet: write to the club, or tick one member (dm)
// or several (group, with an optional name). Mirrors MemberPicker's look.
export function RecipientPicker({
  players,
  meId,
  clubName,
  allowClub = true,
  onPickClub,
  onPickMembers,
  onClose,
}: RecipientPickerProps) {
  const t = useT();
  const ids = useSheetIds();
  // Focus lands in the search box, as `autoFocus` used to put it.
  const search = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (players || [])
      .filter((p: any) => p?.id != null && String(p.id) !== String(meId))
      .filter(
        (p: any) =>
          !n ||
          String(p.name || '')
            .toLowerCase()
            .includes(n),
      )
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), 'nb'))
      .slice(0, 60);
  }, [players, meId, q]);

  const toggle = (id: any) => {
    const s = String(id);
    setPicked((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };
  const pickedNames = picked
    .map((id) => (players || []).find((p: any) => String(p.id) === id)?.name || '')
    .filter(Boolean);

  const start = async () => {
    if (!picked.length || busy) return;
    setBusy(true);
    try {
      await onPickMembers(picked, title);
    } finally {
      setBusy(false);
    }
  };

  return (
    <MobileSheet
      labelledBy={ids.title}
      describedBy={ids.lead}
      onClose={onClose}
      initialFocus={search}
      className="pb-4 flex flex-col max-h-[88%]"
    >
      <div className="flex items-start justify-between mt-3">
        <div>
          <h2
            id={ids.title}
            className="font-display font-semibold text-ink"
            style={{ fontSize: 20 }}
          >
            {t('mobile.newMessage.title')}
          </h2>
          <p id={ids.lead} className="text-[12px] text-ink-3">
            {t('mobile.newMessage.lead')}
          </p>
        </div>
        <button onClick={onClose} aria-label={t('mobile.common.close')} className="p-1 text-ink-3">
          <X size={18} />
        </button>
      </div>

      {allowClub && (
        <button
          onClick={() => void onPickClub()}
          className="mt-3 w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-left"
          style={{ background: '#fff', border: '1px solid #ecefe8' }}
        >
          <span
            className="flex items-center justify-center rounded-full font-display font-bold flex-shrink-0"
            style={{ width: 36, height: 36, background: '#0f1611', color: '#c2f02f' }}
            aria-hidden
          >
            ★
          </span>
          <span className="min-w-0">
            <span className="block text-[14px] font-semibold text-ink truncate">{clubName}</span>
            <span className="block text-[11px] text-ink-3">{t('mobile.newMessage.toClub')}</span>
          </span>
        </button>
      )}

      <div
        className="flex items-center gap-2 rounded-xl px-3 py-2 mt-3"
        style={{ background: '#eef0ea' }}
      >
        <Search size={15} className="text-ink-3" />
        <input
          className="flex-1 bg-transparent text-[13px]"
          placeholder={t('mobile.newMessage.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label={t('mobile.newMessage.search')}
          ref={search}
        />
      </div>

      <div
        className="flex-1 overflow-y-auto mt-2 space-y-0.5 scrollbar-thin"
        style={{ minHeight: 120 }}
      >
        {list.length === 0 && (
          <div className="text-center text-[12px] text-ink-3 py-6">
            {t('mobile.newMessage.noMembers')}
          </div>
        )}
        {list.map((p: any) => {
          const on = picked.includes(String(p.id));
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.id)}
              className="w-full flex items-center gap-3 rounded-xl px-2 py-2 text-left"
              style={{ background: on ? '#fff' : 'transparent' }}
              aria-pressed={on}
            >
              <span
                className="flex items-center justify-center rounded-full font-display font-semibold flex-shrink-0"
                style={{
                  width: 34,
                  height: 34,
                  background: '#dfe3da',
                  color: '#0f1611',
                  fontSize: 12,
                }}
                aria-hidden
              >
                {initials(p.name)}
              </span>
              <span className="flex-1 min-w-0 text-[14px] font-medium text-ink truncate">
                {p.name}
              </span>
              <LevelChip elo={p.elo || 0} compact />
              <span
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{
                  width: 22,
                  height: 22,
                  background: on ? '#c2f02f' : 'transparent',
                  border: on ? 'none' : '1.5px solid #c4ccc6',
                  color: '#0f1611',
                }}
                aria-hidden
              >
                {on && <Check size={13} strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>

      {picked.length > 0 && (
        <div className="pt-3 space-y-2" style={{ borderTop: '1px solid #e7eae3' }}>
          {picked.length > 1 && (
            <input
              className="w-full rounded-xl px-3 py-2 text-[13px]"
              style={{ background: '#fff', border: '1px solid var(--line-strong)' }}
              placeholder={t('mobile.newMessage.groupNameOptional')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-label={t('mobile.newMessage.groupName')}
            />
          )}
          <button
            onClick={() => void start()}
            disabled={busy}
            className="w-full rounded-2xl font-semibold text-[14px] py-3 disabled:opacity-40"
            style={{ background: '#0f1611', color: '#fff' }}
          >
            {picked.length === 1
              ? t('mobile.newMessage.writeTo', {
                  name: pickedNames[0] || t('mobile.newMessage.theMember'),
                })
              : t('mobile.newMessage.startGroup', { count: picked.length })}
          </button>
        </div>
      )}
    </MobileSheet>
  );
}
